import { useCallback, useEffect, useState } from "react";
import { Wallet, ArrowDownToLine, ArrowUpFromLine, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { money, fmtDateTime, PAYMENT_METHOD_LABELS, selectCls, btnBrand } from "@/lib/academyFormat";
import BillCounter, { emptyCount, countTotal, countBody } from "@/components/academy/BillCounter";
import CashChargeModal from "@/components/academy/CashChargeModal";
import { ACADEMY } from "@/constants/testIds";

function MovementModal({ open, onOpenChange, categories, onSaved }) {
  const [form, setForm] = useState({ type: "out", amount: "", reason: "", category: "" });
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setForm({ type: "out", amount: "", reason: "", category: "" }); setError(""); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    try {
      const body = { type: form.type, amount: Number(form.amount), reason: form.reason.trim() };
      if (form.type === "out" && form.category) body.expense_category = form.category;
      await api.post("/academy/cash/movements", body);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={ACADEMY.movementModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Movimiento de caja</DialogTitle>
          <DialogDescription>Plata que sale o entra y no es el pago de un alumno.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Tipo</Label>
              <select className={selectCls} value={form.type} onChange={set("type")} data-testid={ACADEMY.movementTypeSelect}>
                <option value="out">Sale de la caja</option>
                <option value="in">Entra a la caja</option>
              </select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Monto (USD)</Label>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={set("amount")} data-testid={ACADEMY.movementAmountInput} />
            </div>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Motivo</Label>
            <Input value={form.reason} onChange={set("reason")} placeholder="Ej: compra de guantes, cambio" data-testid={ACADEMY.movementReasonInput} />
          </div>
          {form.type === "out" && (
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Si es un gasto, categoría</Label>
              <select className={selectCls} value={form.category} onChange={set("category")} data-testid={ACADEMY.movementCategorySelect}>
                <option value="">No es un gasto (retiro, depósito)</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className={btnBrand} onClick={save} data-testid={ACADEMY.movementSaveButton}>Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Fix V (2026-10-13): la caja de la academia: abrir con el conteo, cobrar,
// movimientos y cerrar comparando lo esperado con lo contado.
export default function AcademyCashPage() {
  const { toast } = useToast();
  const [session, setSession] = useState(undefined);
  const [count, setCount] = useState(emptyCount());
  const [note, setNote] = useState("");
  const [chargeOpen, setChargeOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeError, setCloseError] = useState("");
  const [categories, setCategories] = useState([]);
  const [lastClosed, setLastClosed] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/academy/cash/current");
      setSession(data.session);
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  }, [toast]);

  useEffect(() => {
    load();
    api.get("/academy/settings").then(({ data }) => setCategories(data.expense_categories || [])).catch(() => {});
  }, [load]);

  const openCash = async () => {
    try {
      const { data } = await api.post("/academy/cash/open", { count: countBody(count), note: note.trim() || null });
      setSession(data);
      setCount(emptyCount());
      setNote("");
      setLastClosed(null);
      toast({ title: "Caja abierta", description: `Monto inicial ${money(data.opening_amount)}` });
    } catch (err) {
      toast({ title: "No se pudo abrir", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const closeCash = async () => {
    setCloseError("");
    try {
      const { data } = await api.post("/academy/cash/close", { count: countBody(count), note: note.trim() || null });
      setCloseOpen(false);
      setSession(null);
      setLastClosed(data);
      setCount(emptyCount());
      setNote("");
      toast({ title: "Caja cerrada" });
    } catch (err) {
      setCloseError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (session === undefined) return <p className="text-muted-foreground py-12 text-center">Cargando...</p>;
  const diff = session ? countTotal(count) - session.expected_cash : 0;

  return (
    <div className="space-y-6" data-testid={ACADEMY.cashPage}>
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Caja</h1>
        <p className="text-base text-muted-foreground mt-1">Todo lo que se cobra con la caja abierta queda acá y en la cuenta de cada alumno.</p>
      </div>

      {lastClosed && (
        <div className="bg-[#E3EDE1] text-[#3F5A39] rounded-xl p-4 text-sm" data-testid={ACADEMY.cashClosedBanner}>
          Caja cerrada: esperado {money(lastClosed.expected_amount)}, contado {money(lastClosed.counted_amount)}
          {lastClosed.difference ? `, diferencia ${money(lastClosed.difference)} (${lastClosed.difference_note})` : ", sin diferencia"}.
        </div>
      )}

      {!session ? (
        <div className="bg-white border border-border rounded-xl p-5 space-y-4 max-w-3xl" data-testid={ACADEMY.cashOpenForm}>
          <div className="flex items-center gap-2"><Wallet className="w-5 h-5 text-brand" /><p className="font-medium">Abrir caja</p></div>
          <p className="text-sm text-muted-foreground">Contá los billetes con los que arranca la caja.</p>
          <BillCounter value={count} onChange={setCount} />
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (opcional)" />
          <Button className={btnBrand} onClick={openCash} data-testid={ACADEMY.cashOpenButton}>Abrir caja con {money(countTotal(count))}</Button>
        </div>
      ) : (
        <>
          <div className="bg-white border border-border rounded-xl p-5 space-y-4" data-testid={ACADEMY.cashSession}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-medium">Caja abierta por {session.opened_by_name}</p>
                <p className="text-sm text-muted-foreground">{fmtDateTime(session.opened_at)} · inicial {money(session.opening_amount)}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button className={btnBrand} onClick={() => setChargeOpen(true)} data-testid={ACADEMY.cashChargeButton}><ArrowDownToLine className="w-4 h-4" /> Cobrar</Button>
                <Button variant="outline" onClick={() => setMoveOpen(true)} data-testid={ACADEMY.cashMovementButton}><ArrowUpFromLine className="w-4 h-4" /> Movimiento</Button>
                <Button variant="outline" onClick={() => { setCloseError(""); setCloseOpen(true); }} data-testid={ACADEMY.cashCloseButton}><Lock className="w-4 h-4" /> Cerrar caja</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg bg-[#E9F3F3] p-3"><p className="text-xs text-muted-foreground">Efectivo esperado</p><p className="font-medium text-lg" data-testid={ACADEMY.cashExpected}>{money(session.expected_cash)}</p></div>
              {["cash", "card", "zelle"].map((m) => (
                <div key={m} className="rounded-lg bg-[#E9F3F3] p-3"><p className="text-xs text-muted-foreground">Cobrado en {m === "zelle" ? "Zelle" : PAYMENT_METHOD_LABELS[m].toLowerCase()}</p><p className="font-medium">{money(session.by_method[m] || 0)}</p></div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-border rounded-xl overflow-x-auto">
            <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground px-5 pt-5 pb-3">Movimientos de esta caja</p>
            {session.payments.length === 0 && session.movements.length === 0 ? (
              <p className="text-sm text-muted-foreground px-5 pb-5">Todavía no hay movimientos.</p>
            ) : (
              <table className="w-full text-sm" data-testid={ACADEMY.cashTable}>
                <tbody>
                  {session.payments.map((p) => (
                    <tr key={p.id} className={`border-t border-border ${p.status === "voided" ? "opacity-50 line-through" : ""}`}>
                      <td className="py-2 px-5">{fmtDateTime(p.created_at)}</td>
                      <td className="py-2 px-3">{p.student_name}{p.is_reservation ? " · reserva" : ""}</td>
                      <td className="py-2 px-3">{PAYMENT_METHOD_LABELS[p.method] || p.method}</td>
                      <td className="py-2 px-5 text-right font-medium text-[#5B7A54]">+{money(p.amount)}</td>
                    </tr>
                  ))}
                  {session.movements.map((m) => (
                    <tr key={m.id} className="border-t border-border">
                      <td className="py-2 px-5">{fmtDateTime(m.at)}</td>
                      <td className="py-2 px-3">{m.reason}{m.expense_id ? " (gasto)" : ""}</td>
                      <td className="py-2 px-3">Efectivo · {m.by_name}</td>
                      <td className={`py-2 px-5 text-right font-medium ${m.type === "out" ? "text-[#A34B49]" : "text-[#5B7A54]"}`}>{m.type === "out" ? "-" : "+"}{money(m.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      <CashChargeModal open={chargeOpen} onOpenChange={setChargeOpen} onCharged={() => { toast({ title: "Cobro registrado" }); load(); }} />
      <MovementModal open={moveOpen} onOpenChange={setMoveOpen} categories={categories} onSaved={() => { toast({ title: "Movimiento registrado" }); load(); }} />
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={ACADEMY.cashCloseModal}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Cerrar caja</DialogTitle>
            <DialogDescription>Contá el efectivo. Tiene que haber {session ? money(session.expected_cash) : ""}.</DialogDescription>
          </DialogHeader>
          <BillCounter value={count} onChange={setCount} />
          {session && Math.abs(diff) >= 0.01 && (
            <p className="text-sm text-[#A34B49]" data-testid={ACADEMY.cashDifference}>{diff > 0 ? "Sobran" : "Faltan"} {money(Math.abs(diff))}. Explicá qué pasó para poder cerrar.</p>
          )}
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota del cierre" data-testid={ACADEMY.cashCloseNote} />
          {closeError && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{closeError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>Volver</Button>
            <Button className={btnBrand} onClick={closeCash} data-testid={ACADEMY.cashCloseConfirm}>Cerrar caja</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
